/**
 * A casca do sistema: confere se há alguém logado, carrega os cadastros
 * (usados por todas as telas), desenha o topo com o menu, o botão "Relatar"
 * e a logo, e escolhe a tela pela rota (rotas.ts).
 * Quem entrou com senha provisória só vê a tela "Crie a sua senha".
 *
 * Lê: api.sessao e api.cadastros.
 * Usado por: main.tsx.
 */
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Aviso, ErroNaTela } from './componentes/basicos.tsx';
import { BotaoRelatar } from './componentes/BotaoRelatar.tsx';
import { LimiteDeErro } from './componentes/LimiteDeErro.tsx';
import { api, ErroApi, quandoPedirSenhaNova, quandoPerderSessao } from './dados/api.ts';
import type { Cadastros as TipoCadastros, Usuario } from './dominio/tipos.ts';
import { linkPara, usarRota, type Rota } from './rotas.ts';
import { Cadastros } from './telas/Cadastros.tsx';
import { Conta } from './telas/Conta.tsx';
import { Entrar } from './telas/Entrar.tsx';
import { Importacoes } from './telas/Importacoes.tsx';
import { Painel } from './telas/Painel.tsx';
import { Relatos } from './telas/Relatos.tsx';
import { Representante } from './telas/Representante.tsx';
import { Sistema } from './telas/Sistema.tsx';
import { VERSAO_DO_SITE } from './versao.ts';

/**
 * Carrega uma tela sob demanda. Depois de uma atualização do site, uma aba que
 * ficou aberta pede arquivos antigos que não existem mais ("Failed to fetch
 * dynamically imported module"): nesse caso a página recarrega uma vez sozinha.
 */
function carregarTela<T>(importar: () => Promise<T>): () => Promise<T> {
  const marca = 'alianca-recarregou-apos-atualizacao';
  return () =>
    importar()
      .then((modulo) => {
        try {
          sessionStorage.removeItem(marca);
        } catch {
          // sem sessionStorage (aba anônima): tudo bem
        }
        return modulo;
      })
      .catch((erro: unknown) => {
        let jaRecarregou = true;
        try {
          jaRecarregou = sessionStorage.getItem(marca) === '1';
          if (!jaRecarregou) sessionStorage.setItem(marca, '1');
        } catch {
          // sem sessionStorage: não arrisca recarregar em laço
        }
        if (jaRecarregou) throw erro;
        window.location.reload();
        return new Promise<T>(() => {});
      });
}

// As telas que leem planilhas carregam a biblioteca de planilhas (grande) só quando abertas.
const Importar = lazy(carregarTela(() => import('./telas/Importar.tsx').then((m) => ({ default: m.Importar }))));
const Metas = lazy(carregarTela(() => import('./telas/Metas.tsx').then((m) => ({ default: m.Metas }))));

export function App() {
  const rota = usarRota();
  /** undefined = ainda conferindo a sessão; null = ninguém logado. */
  const [usuario, setUsuario] = useState<Usuario | null | undefined>(undefined);
  const [cadastros, setCadastros] = useState<TipoCadastros | null>(null);
  const [erro, setErro] = useState<ErroApi | null>(null);
  /** A logo vem com a sessão (aparece antes do login); depois, a dos cadastros (atualiza quando troca). */
  const [logoDaSessao, setLogoDaSessao] = useState<string | null>(null);
  /** A sessão acabou sozinha (tempo sem uso, senha trocada em outro aparelho): a tela de entrada avisa. */
  const [sessaoTerminou, setSessaoTerminou] = useState(false);

  // A altura do topo (muda no celular, com logo) vira a variável --altura-topo do CSS:
  // as mensagens grudadas no alto da tela ficam logo abaixo dele, nunca escondidas.
  const medirTopo = useCallback((topo: HTMLElement | null) => {
    if (!topo || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(() => document.documentElement.style.setProperty('--altura-topo', `${topo.offsetHeight}px`));
    observador.observe(topo);
    return () => observador.disconnect();
  }, []);

  useEffect(() => {
    quandoPerderSessao(() => {
      setSessaoTerminou(true);
      setUsuario(null);
      setCadastros(null);
    });
    // Senha virou provisória (um administrador gerou uma nova): confere de novo quem está logado.
    quandoPedirSenhaNova(() => {
      api
        .sessao()
        .then(({ usuario }) => setUsuario(usuario))
        .catch(() => setUsuario(null));
    });
    api
      .sessao()
      .then(({ usuario, logo }) => {
        setLogoDaSessao(logo);
        setUsuario(usuario);
      })
      .catch((falha: unknown) => {
        setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
        setUsuario(null);
      });
  }, []);

  const carregarCadastros = useCallback(() => {
    api
      .cadastros()
      .then((novos) => {
        setErro(null);
        setCadastros(novos);
      })
      .catch((falha: unknown) => setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null)));
  }, []);

  // Com senha provisória a API não entrega os cadastros (API-428): só depois da senha nova.
  const liberado = usuario !== null && usuario !== undefined && !usuario.senhaProvisoria;
  useEffect(() => {
    if (liberado) carregarCadastros();
  }, [liberado, carregarCadastros]);

  const logo = cadastros ? (cadastros.logo ?? null) : logoDaSessao;
  // A logo da tela de entrada acompanha a dos cadastros (trocar ou remover a logo e sair mostra a nova).
  useEffect(() => {
    if (cadastros) setLogoDaSessao(cadastros.logo ?? null);
  }, [cadastros]);

  if (usuario === undefined) return <p className="pagina texto-apagado">Carregando…</p>;
  if (usuario === null)
    return (
      <>
        {erro && erro.codigo !== 'API-401' ? (
          <div className="pagina">
            <ErroNaTela erro={erro} />
          </div>
        ) : null}
        <Entrar
          logo={logo}
          sessaoTerminou={sessaoTerminou}
          aoEntrar={(quem) => {
            setErro(null); // um erro de antes do login não vale mais
            setSessaoTerminou(false);
            setUsuario(quem);
          }}
        />
      </>
    );

  async function sair() {
    try {
      await api.sair();
    } catch (falha) {
      // Sem resposta do servidor a sessão continuaria aberta lá: avisa em vez de fingir que saiu.
      if (!(falha instanceof ErroApi && falha.status === 401)) {
        setErro(new ErroApi(falha instanceof ErroApi ? falha.codigo : 'APP', `Não consegui sair: ${falha instanceof Error ? falha.message : String(falha)}`, 0, null));
        window.scrollTo(0, 0);
        return;
      }
    }
    setErro(null);
    setSessaoTerminou(false);
    setUsuario(null);
    setCadastros(null);
  }

  const marcaDoTopo = (
    <a className="logo" href={linkPara({ tela: 'painel', mes: '' })}>
      {logo ? <img className="logo-imagem" src={logo} alt="Aliança" /> : 'aliança'}
      <small>metas</small>
    </a>
  );

  if (usuario.senhaProvisoria) {
    return (
      <>
        <header className="topo" ref={medirTopo}>
          <div className="topo-conteudo">
            {marcaDoTopo}
            <div className="usuario">
              <button type="button" className="botao-secundario" onClick={() => void sair()}>
                Sair
              </button>
            </div>
          </div>
        </header>
        {erro ? (
          <div className="pagina">
            <ErroNaTela erro={erro} />
          </div>
        ) : null}
        <Conta usuario={usuario} aoTrocar={setUsuario} />
      </>
    );
  }

  const mes = 'mes' in rota ? rota.mes : undefined;
  // O menu "Painel" volta para o painel que estava aberto (Aliança ou Bauny).
  const fonte = 'fonte' in rota ? rota.fonte : undefined;
  const menu: Array<{ rotulo: string; rota: Rota; ativo: boolean }> = [
    { rotulo: 'Painel', rota: { tela: 'painel', mes: mes ?? '', fonte }, ativo: rota.tela === 'painel' || rota.tela === 'representante' },
    { rotulo: 'Importar', rota: { tela: 'importar' }, ativo: rota.tela === 'importar' || rota.tela === 'importacoes' },
    { rotulo: 'Metas', rota: { tela: 'metas', mes: mes ?? '' }, ativo: rota.tela === 'metas' },
    { rotulo: 'Cadastros', rota: { tela: 'cadastros', mes: mes ?? '' }, ativo: rota.tela === 'cadastros' },
    { rotulo: 'Relatos', rota: { tela: 'relatos' }, ativo: rota.tela === 'relatos' },
    ...(usuario.papel === 'admin' ? [{ rotulo: 'Sistema', rota: { tela: 'sistema' } as Rota, ativo: rota.tela === 'sistema' }] : []),
  ];

  return (
    <>
      <header className="topo" ref={medirTopo}>
        <div className="topo-conteudo">
          {marcaDoTopo}
          <nav className="navegacao" aria-label="Menu">
            {menu.map((item) => (
              <a key={item.rotulo} href={linkPara(item.rota).replace(/\/$/, '')} aria-current={item.ativo ? 'page' : undefined}>
                {item.rotulo}
              </a>
            ))}
          </nav>
          <div className="usuario">
            <BotaoRelatar />
            <a href={linkPara({ tela: 'conta' })} title="Minha senha" aria-current={rota.tela === 'conta' ? 'page' : undefined}>
              <span className="so-tela-grande">{usuario.nome}</span>
              <span className="so-celular">Conta</span>
            </a>
            <button type="button" className="botao-secundario" onClick={() => void sair()}>
              Sair
            </button>
          </div>
        </div>
      </header>
      {erro ? (
        <div className="pagina">
          <ErroNaTela erro={erro} />
        </div>
      ) : null}
      {cadastros ? (
        <LimiteDeErro key={linkPara(rota)}>
          <Suspense fallback={<p className="pagina texto-apagado">Carregando…</p>}>
            <Tela rota={rota} usuario={usuario} cadastros={cadastros} aoAtualizarCadastros={setCadastros} aoAtualizarUsuario={setUsuario} />
          </Suspense>
        </LimiteDeErro>
      ) : (
        <p className="pagina texto-apagado">Carregando…</p>
      )}
      <footer className="rodape">Metas Aliança · versão {VERSAO_DO_SITE}</footer>
    </>
  );
}

interface PropsDaTela {
  rota: Rota;
  usuario: Usuario;
  cadastros: TipoCadastros;
  aoAtualizarCadastros: (c: TipoCadastros) => void;
  aoAtualizarUsuario: (u: Usuario) => void;
}

function Tela({ rota, usuario, cadastros, aoAtualizarCadastros, aoAtualizarUsuario }: PropsDaTela) {
  switch (rota.tela) {
    case 'relatos':
      return <Relatos usuario={usuario} />;
    case 'conta':
      return <Conta usuario={usuario} aoTrocar={aoAtualizarUsuario} />;
    case 'sistema':
      return usuario.papel === 'admin' ? (
        <Sistema usuario={usuario} />
      ) : (
        <div className="pagina">
          <Aviso tipo="info">A tela Sistema é só para administrador.</Aviso>
        </div>
      );
    case 'painel':
      return <Painel mes={rota.mes} fonte={rota.fonte ?? 'winthor'} cadastros={cadastros} />;
    case 'representante':
      return <Representante id={rota.id} mes={rota.mes} fonte={rota.fonte ?? 'winthor'} cadastros={cadastros} />;
    case 'importar':
      return <Importar cadastros={cadastros} />;
    case 'importacoes':
      return <Importacoes />;
    case 'metas':
      return <Metas mes={rota.mes} cadastros={cadastros} />;
    case 'cadastros':
      return <Cadastros mes={rota.mes} cadastros={cadastros} aoAtualizar={aoAtualizarCadastros} />;
  }
}
